import { CONTENT_GROUP_TABS, GROUP_TAB_NAMES, type GroupTabName } from './constants'
import type { BuildTabsConfigurationPayloadInputItem, TabConfigurationItem, UpdateGroupTabsConfigurationItem } from '../types/types'

interface NormalizeGroupTabsOptions {
  isGroupAdmin?: boolean
}

function isGroupTabName(name: string): name is GroupTabName {
  return GROUP_TAB_NAMES.includes(name as GroupTabName)
}

function applyAdminVisibility(
  tabs: Array<TabConfigurationItem & { id: string; sequence: number }>,
  isGroupAdmin: boolean
): Array<TabConfigurationItem & { id: string; sequence: number }> {
  if (!isGroupAdmin) return tabs

  return tabs.map(tab => (tab.name === 'MEMBERS' ? { ...tab, isVisible: true } : tab))
}

function getDefaultTabConfiguration(): Array<TabConfigurationItem & { id: string; sequence: number }> {
  return CONTENT_GROUP_TABS.map(({ id, name }, sequence) => ({
    id,
    name,
    isVisible: true,
    sequence,
  }))
}

function isLegacyTabConfigurationNameArray(tabsConfiguration: TabConfigurationItem[] | string[]): tabsConfiguration is string[] {
  return tabsConfiguration.length > 0 && typeof tabsConfiguration[0] === 'string'
}

/**
 * Converts both legacy visible-tab enum arrays and current tab objects into
 * a complete ordered tab list. Missing tabs default to visible so existing
 * groups keep their previous all-tabs-enabled behavior.
 */
export function getNormalizedGroupTabsConfiguration(
  tabsConfiguration: TabConfigurationItem[] | string[] | undefined,
  options: NormalizeGroupTabsOptions = {}
): Array<TabConfigurationItem & { id: string; sequence: number }> {
  if (!tabsConfiguration || tabsConfiguration.length === 0) return getDefaultTabConfiguration()

  if (isLegacyTabConfigurationNameArray(tabsConfiguration)) {
    // Legacy format: ['DISCUSSION'] -> [{ id: 'home', name: 'DISCUSSION', isVisible: true, sequence: 0 }]
    const visibleNames = new Set(tabsConfiguration)
    return applyAdminVisibility(
      getDefaultTabConfiguration().map(tab => ({
        ...tab,
        isVisible: visibleNames.has(tab.name),
      })),
      options.isGroupAdmin ?? false
    )
  }

  return applyAdminVisibility(
    CONTENT_GROUP_TABS.map(({ id, name }, defaultSequence) => {
      const tab = tabsConfiguration.find(item => item.name === name)

      return {
        id,
        name,
        isVisible: tab?.isVisible ?? true,
        sequence: tab?.sequence ?? defaultSequence,
        ...(tab?.customLabel?.trim() ? { customLabel: tab.customLabel.trim() } : {}),
      }
    }).sort((a, b) => a.sequence - b.sequence || GROUP_TAB_NAMES.indexOf(a.name) - GROUP_TAB_NAMES.indexOf(b.name)),
    options.isGroupAdmin ?? false
  )
}

/**
 * Build tabsConfiguration payload for API.
 * Accepts ordered tab items from settings UI and maps them to backend tab enum configuration.
 * Output preserves input order and assigns sequence from the current item position.
 */
export function buildTabsConfigurationPayload(tabs: BuildTabsConfigurationPayloadInputItem[]): UpdateGroupTabsConfigurationItem[] {
  const payload = tabs.flatMap(tab => {
    if (!isGroupTabName(tab.name)) return []
    return [
      {
        name: tab.name,
        isVisible: tab.isVisible,
        ...(tab.customLabel?.trim() ? { customLabel: tab.customLabel.trim() } : {}),
      },
    ]
  })

  if (payload.length !== tabs.length) {
    throw new Error('Invalid group tabs configuration payload')
  }

  if (payload.length !== GROUP_TAB_NAMES.length) {
    throw new Error('Incomplete group tabs configuration payload')
  }

  return payload.map((tab, sequence) => ({
    ...tab,
    sequence,
  }))
}
